import React, { useState } from 'react';
import { Layers, Zap, Heart } from 'lucide-react';

function normalizeZones(rawZones, isPower = false) {
    if (!rawZones || typeof rawZones !== 'object') return null;

    const defaults = isPower ? {
        z1: { name: 'Recuperación Activa', color: '#6b7280' },
        z2: { name: 'Resistencia (Fondo)', color: '#3b82f6' },
        z3: { name: 'Tempo', color: '#10b981' },
        z4: { name: 'Umbral Lactato', color: '#eab308' },
        z5: { name: 'VO2 Máx', color: '#f97316' },
        z6: { name: 'Capacidad Anaeróbica', color: '#ef4444' },
        z7: { name: 'Neuromuscular', color: '#a855f7' }
    } : {
        z1: { name: 'Recuperación Activa', color: '#6b7280' },
        z2: { name: 'Fondo Aeróbico', color: '#3b82f6' },
        z3: { name: 'Tempo', color: '#10b981' },
        z4: { name: 'Umbral Lactato', color: '#f97316' },
        z5: { name: 'Anaeróbico / VO2', color: '#ef4444' }
    };

    const entries = Object.entries(rawZones);
    if (entries.length === 0) return null;

    let totalSecs = 0;
    const parsed = entries.map(([key, val]) => {
        const lowerKey = key.toLowerCase();
        const def = defaults[lowerKey] || { name: key.toUpperCase(), color: '#3b82f6' };

        let seconds = 0;
        let percent = 0;
        let min = undefined;
        let max = undefined;

        if (typeof val === 'number') {
            // Si el valor es pequeño (< 500), viene en minutos desde Strava hrZonesTime
            seconds = val > 500 ? Math.round(val) : Math.round(val * 60);
        } else if (val && typeof val === 'object') {
            seconds = Number(val.seconds) || (val.time ? Math.round(val.time) : 0);
            percent = Number(val.percent) || 0;
            min = val.min;
            max = val.max;
        }

        totalSecs += seconds;
        return {
            key: lowerKey,
            name: (typeof val === 'object' && val?.name) || def.name,
            color: (typeof val === 'object' && val?.color) || def.color,
            seconds,
            percent,
            min,
            max
        };
    });

    if (totalSecs === 0) return null;

    return parsed.map(z => ({
        ...z,
        percent: z.percent > 0 ? z.percent : parseFloat(((z.seconds / totalSecs) * 100).toFixed(1))
    }));
}

export default function ZonesDistributionWidget({ powerZonesTime, hrZonesTime }) {
    const normPower = normalizeZones(powerZonesTime, true);
    const normHr = normalizeZones(hrZonesTime, false);

    const hasPower = Boolean(normPower && normPower.length > 0);
    const hasHr = Boolean(normHr && normHr.length > 0);

    const [activeTab, setActiveTab] = useState(hasPower ? 'power' : 'hr');

    if (!hasPower && !hasHr) return null;

    const currentList = activeTab === 'power' ? (normPower || normHr) : (normHr || normPower);

    const formatDuration = (seconds) => {
        if (!seconds || seconds <= 0) return '0s';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    return (
        <div style={{
            background: 'rgba(255, 255, 255, 0.015)',
            border: '1px solid var(--glass-border)',
            borderRadius: '12px',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.85rem'
        }}>
            {/* Header con selector */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        padding: '6px',
                        borderRadius: '8px',
                        background: 'rgba(56, 189, 248, 0.1)',
                        border: '1px solid rgba(56, 189, 248, 0.2)',
                        color: '#38bdf8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Layers size={16} />
                    </div>
                    <div>
                        <h4 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                            Distribución de Intensidad
                        </h4>
                        <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0 }}>
                            Tiempo acumulado en zonas {activeTab === 'power' ? 'de potencia (Coggan)' : 'cardíacas (Friel)'}
                        </p>
                    </div>
                </div>

                {hasPower && hasHr && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: '8px',
                        padding: '2px'
                    }}>
                        <button
                            type="button"
                            onClick={() => setActiveTab('power')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                border: activeTab === 'power' ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid transparent',
                                background: activeTab === 'power' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                                color: activeTab === 'power' ? '#f59e0b' : 'var(--text-secondary)',
                                fontSize: '0.72rem',
                                fontWeight: activeTab === 'power' ? 700 : 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Zap size={12} />
                            <span>Vatios</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('hr')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                border: activeTab === 'hr' ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid transparent',
                                background: activeTab === 'hr' ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
                                color: activeTab === 'hr' ? '#ef4444' : 'var(--text-secondary)',
                                fontSize: '0.72rem',
                                fontWeight: activeTab === 'hr' ? 700 : 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Heart size={12} />
                            <span>Pulso</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Lista de Barras de Zonas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {currentList && currentList.map(zone => {
                    const durationStr = formatDuration(zone.seconds || 0);
                    const rangeStr = zone.min !== undefined && zone.max !== undefined
                        ? (zone.max > 2000 ? `> ${zone.min} ${activeTab === 'power' ? 'W' : 'ppm'}` : `${zone.min}-${zone.max} ${activeTab === 'power' ? 'W' : 'ppm'}`)
                        : '';

                    return (
                        <div
                            key={zone.key}
                            style={{
                                background: 'rgba(0, 0, 0, 0.2)',
                                border: '1px solid rgba(255, 255, 255, 0.04)',
                                borderRadius: '8px',
                                padding: '0.45rem 0.7rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{
                                        fontFamily: 'monospace',
                                        fontWeight: 800,
                                        fontSize: '0.65rem',
                                        padding: '1px 5px',
                                        borderRadius: '4px',
                                        background: `${zone.color}20`,
                                        color: zone.color,
                                        border: `1px solid ${zone.color}40`
                                    }}>
                                        {zone.key.toUpperCase()}
                                    </span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                        {zone.name}
                                    </span>
                                    {rangeStr && (
                                        <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                                            ({rangeStr})
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'monospace' }}>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
                                        {durationStr}
                                    </span>
                                    <span style={{ fontWeight: 700, color: '#fff', width: '36px', textAlign: 'right', fontSize: '0.75rem' }}>
                                        {zone.percent}%
                                    </span>
                                </div>
                            </div>

                            {/* Barra de progreso */}
                            <div style={{
                                width: '100%',
                                height: '5px',
                                background: 'rgba(255, 255, 255, 0.06)',
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}>
                                <div
                                    style={{
                                        width: `${Math.min(100, Math.max(zone.percent > 0 ? 2 : 0, zone.percent))}%`,
                                        height: '100%',
                                        background: zone.color,
                                        borderRadius: '3px',
                                        transition: 'width 0.4s ease'
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
